import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getRefundStatusBadgeClass, getStatusBadgeClass } from "./badges";
import useGetMyTransactions, { useGetCarPaymentRecords } from "../../../hooks/invoice/getMyTransactions";
import moment from "moment-timezone";
import useGetUserRefunds from "../../../hooks/invoice/useGetUserRefunds";
import { toast } from "react-toastify";
const TransactionDetailTable = () => {
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { transactions, loading, error, fetchTransactions } =
    useGetMyTransactions();
  const { carPayments, loading: carPaymentsLoading, error: carPaymentsError, fetchCarPayments } = useGetCarPaymentRecords();
  const [sortOrder, setSortOrder] = useState("latest");
  const { refunds, loading: refundsLoading, error: refundsError, fetchRefunds } = useGetUserRefunds();

  useEffect(() => {
    fetchTransactions();
    fetchCarPayments();
    fetchRefunds();
  }, [fetchTransactions, fetchCarPayments, fetchRefunds]);

  const handleSort = (order) => {
    setSortOrder(order);
    setIsDropdownOpen(false);
  };

const handleCarDetailsClick = (transaction) => {
  try {
    // Parse details if it's a string, otherwise use directly
    const details = typeof transaction.details === 'string' 
      ? JSON.parse(transaction.details) 
      : transaction.details;

    if (transaction.type === 'Local-car') {
      // For local cars, use car_id for redirection
      if (transaction.car_id) {
        navigate(`/local-vehicle-detail/${transaction.car_id}`);
      } else {
        toast.error('Car details not available for redirection');
      }
    } else if (transaction.type === 'Import-car') {
      // For import cars, use lot_id from details for redirection
      const lotId = details?.lot_id;
      if (lotId) {
        navigate(`/vehicle-detail/${lotId}`);
      } else {
        toast.error('Car details not available for redirection');
      }
    }
  } catch (error) {
    toast.error('Error processing car details');
  }
};

  if (loading || carPaymentsLoading || refundsLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full lg:rounded-[100%] h-12 lg:h-[3vw] lg:w-[3vw] w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Add error state
  if (error || carPaymentsError || refundsError) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-red-500 text-center">
          <p>Error loading data: {error || carPaymentsError || refundsError}</p>
          <button
            onClick={() => { fetchTransactions(); fetchCarPayments(); fetchRefunds(); }}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-[90vw] lg:w-[85vw] px-4 mx-auto sm:px-6 lg:px-[2vw] my-4 lg:my-[1vw]">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div className="">
            <h1 className="text-[24px] md:text-[36px] lg:text-[2.3vw] text-left font-semibold text-gray-900">
              Transaction History
            </h1>
          </div>
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none flex items-center gap-4 lg:gap-[1vw]">
            <div className="relative inline-block text-left">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 lg:px-[1vw] py-2 lg:py-[0.5vw] text-sm lg:text-[0.875vw] font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                Sort by {sortOrder === "latest" ? "Latest" : "Oldest"}
                <svg
                  className="-mr-1 ml-2 h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {isDropdownOpen && (
                <div className="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="py-1 lg:py-[0.25vw]">
                    <button
                      onClick={() => handleSort("latest")}
                      className={`block px-4 lg:px-[1vw] py-2 lg:py-[0.5vw] text-sm lg:text-[0.875vw] w-full lg:w-[100%] text-left ${sortOrder === "latest" ? "bg-gray-100 text-gray-900" : "text-gray-700"}`}
                    >
                      Latest
                    </button>
                    <button
                      onClick={() => handleSort("oldest")}
                      className={`block px-4 lg:px-[1vw] py-2 lg:py-[0.5vw] text-sm lg:text-[0.875vw] w-full lg:w-[100%] text-left ${sortOrder === "oldest" ? "bg-gray-100 text-gray-900" : "text-gray-700"}`}
                    >
                      Oldest
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Transaction History Table */}
        <div className="mt-8 lg:mt-[2vw] flex flex-col">
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="shadow-[1vw] ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <div className="max-h-[520px] overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 text-nowrap lg:py-[0.875vw] pl-2 lg:pl-[1vw] pr-3 lg:pr-[0.75vw] text-left text-sm lg:text-[0.875vw] leading-[1.25vw] font-medium text-gray-500 sm:pl-6">
                          TRANSACTION ID
                        </th>
                        <th scope="col" className="py-3.5 text-nowrap lg:py-[0.875vw] pl-2 lg:pl-[1vw] pr-3 lg:pr-[0.75vw] text-left text-sm lg:text-[0.875vw] leading-[1.25vw] font-medium text-gray-500 sm:pl-6">
                          DATE
                        </th>
                        <th scope="col" className="py-3.5 text-nowrap lg:py-[0.875vw] pl-2 lg:pl-[1vw] pr-3 lg:pr-[0.75vw] text-left text-sm lg:text-[0.875vw] leading-[1.25vw] font-medium text-gray-500 sm:pl-6">
                          PAYMENT PURPOSE
                        </th>
                        <th scope="col" className="py-3.5 text-nowrap lg:py-[0.875vw] pl-2 lg:pl-[1vw] pr-3 lg:pr-[0.75vw] text-left text-sm lg:text-[0.875vw] leading-[1.25vw] font-medium text-gray-500 sm:pl-6">
                          AMOUNT (USD)
                        </th>
                        <th scope="col" className="py-3.5 text-nowrap lg:py-[0.875vw] pl-2 lg:pl-[1vw] pr-3 lg:pr-[0.75vw] text-left text-sm lg:text-[0.875vw] leading-[1.25vw] font-medium text-gray-500 sm:pl-6">
                          STATUS
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {transactions?.data && transactions.data.length > 0 ? (
                        [...transactions.data]
                          .sort((a, b) => {
                            const dateA = new Date(a.createdAt);
                            const dateB = new Date(b.createdAt);
                            return sortOrder === "latest" ? dateB - dateA : dateA - dateB;
                          })
                          .map((transaction, index) => (
                            <tr key={index}>
                              <td className="px-3 lg:px-[0.75vw] py-4 lg:py-[1vw] text-sm lg:text-[0.875vw] text-left font-medium text-gray-900 sm:pl-6">
                                {transaction?.id || "N/A"}
                                <svg
                                  className="inline-block ml-1 h-4 w-4 text-gray-400"
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path d="M8 2a1 1 0 000 2h2a1 1 0 100-2H8z" />
                                  <path d="M3 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v6h-4.586l1.293-1.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L10.414 13H15v3a2 2 0 01-2 2H5a2 2 0 01-2-2V5zM15 11h2a1 1 0 110 2h-2v-2z" />
                                </svg>
                              </td>
                              <td className="whitespace-nowrap text-left px-3 lg:px-[0.75vw] py-4 lg:py-[1vw] text-sm lg:text-[0.875vw] text-gray-500">
                                {moment(transaction?.createdAt).format("DD-MM-YYYY HH:mm") || "N/A"}
                              </td>
                              <td className="whitespace-nowrap text-left px-3 lg:px-[0.75vw] py-4 lg:py-[1vw] text-sm lg:text-[0.875vw] text-gray-500">
                                {transaction?.paymentPurpose || "N/A"}
                              </td>
                              <td className="whitespace-nowrap text-left px-3 lg:px-[0.75vw] py-4 lg:py-[1vw] text-sm lg:text-[0.875vw] text-gray-500">
                                ${transaction?.amount || "N/A"}
                              </td>
                              <td className="whitespace-nowrap text-left px-3 lg:px-[0.75vw] py-4 lg:py-[1vw] text-sm lg:text-[0.875vw] text-gray-500">
                                <span className={`inline-flex rounded-full lg:rounded-[0.5vw] px-2 lg:px-[0.5vw] text-xs lg:text-[0.75vw] font-semibold leading-5 lg:leading-[1.25vw] ${getStatusBadgeClass(transaction?.paymentStatus)}`}>
                                  {transaction?.paymentStatus || "N/A"}
                                </span>
                              </td>
                            </tr>
                          ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="px-3 lg:px-[0.75vw] py-6 text-center text-sm lg:text-[0.875vw] text-gray-500">
                            No transactions found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Refund Records Table */}
        <div className="mt-10 lg:mt-[2.5vw] flex flex-col">
          <h1 className="text-[24px] md:text-[36px] lg:text-[2.3vw] text-left font-semibold text-gray-900 mb-4">Refund Record</h1>
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="shadow-[1vw] ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <div className="max-h-[520px] overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-3.5 text-nowrap lg:py-[0.875vw] pl-2 lg:pl-[1vw] pr-3 lg:pr-[0.75vw] text-left text-sm lg:text-[0.875vw] leading-[1.25vw] font-medium text-gray-500 sm:pl-6">PAYMENT METHOD</th>
                        <th className="py-3.5 text-nowrap lg:py-[0.875vw] pl-2 lg:pl-[1vw] pr-3 lg:pr-[0.75vw] text-left text-sm lg:text-[0.875vw] leading-[1.25vw] font-medium text-gray-500 sm:pl-6">BANK NAME</th>
                        <th className="py-3.5 text-nowrap lg:py-[0.875vw] pl-2 lg:pl-[1vw] pr-3 lg:pr-[0.75vw] text-left text-sm lg:text-[0.875vw] leading-[1.25vw] font-medium text-gray-500 sm:pl-6">ACCOUNT NUMBER</th>
                        <th className="py-3.5 text-nowrap lg:py-[0.875vw] pl-2 lg:pl-[1vw] pr-3 lg:pr-[0.75vw] text-left text-sm lg:text-[0.875vw] leading-[1.25vw] font-medium text-gray-500 sm:pl-6">BRANCH NUMBER</th>
                        <th className="py-3.5 text-nowrap lg:py-[0.875vw] pl-2 lg:pl-[1vw] pr-3 lg:pr-[0.75vw] text-left text-sm lg:text-[0.875vw] leading-[1.25vw] font-medium text-gray-500 sm:pl-6">TRANSIT NUMBER</th>
                        <th className="py-3.5 text-nowrap lg:py-[0.875vw] pl-2 lg:pl-[1vw] pr-3 lg:pr-[0.75vw] text-left text-sm lg:text-[0.875vw] leading-[1.25vw] font-medium text-gray-500 sm:pl-6">PROCESSING FEE</th>
                        <th className="py-3.5 text-nowrap lg:py-[0.875vw] pl-2 lg:pl-[1vw] pr-3 lg:pr-[0.75vw] text-left text-sm lg:text-[0.875vw] leading-[1.25vw] font-medium text-gray-500 sm:pl-6">REFUND AMOUNT</th>
                        <th className="py-3.5 text-nowrap lg:py-[0.875vw] pl-2 lg:pl-[1vw] pr-3 lg:pr-[0.75vw] text-left text-sm lg:text-[0.875vw] leading-[1.25vw] font-medium text-gray-500 sm:pl-6">STATUS</th>
                        <th className="py-3.5 text-nowrap lg:py-[0.875vw] pl-2 lg:pl-[1vw] pr-3 lg:pr-[0.75vw] text-left text-sm lg:text-[0.875vw] leading-[1.25vw] font-medium text-gray-500 sm:pl-6">CREATED AT</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {refunds?.data && refunds.data.length > 0 ? (
                        refunds.data.map((refund) => (
                          <tr key={refund.id}>
                            <td className="px-3 lg:px-[0.75vw] py-4 lg:py-[1vw] text-sm lg:text-[0.875vw] text-gray-500">{refund.payment_method || 'N/A'}</td>
                            <td className="px-3 lg:px-[0.75vw] py-4 lg:py-[1vw] text-sm lg:text-[0.875vw] text-gray-500">{refund.bank_name || 'N/A'}</td>
                            <td className="px-3 lg:px-[0.75vw] py-4 lg:py-[1vw] text-sm lg:text-[0.875vw] text-gray-500">{refund.account_number || 'N/A'}</td>
                            <td className="px-3 lg:px-[0.75vw] py-4 lg:py-[1vw] text-sm lg:text-[0.875vw] text-gray-500">{refund.branch_number || 'N/A'}</td>
                            <td className="px-3 lg:px-[0.75vw] py-4 lg:py-[1vw] text-sm lg:text-[0.875vw] text-gray-500">{refund.transit_number || 'N/A'}</td>
                            <td className="px-3 lg:px-[0.75vw] py-4 lg:py-[1vw] text-sm lg:text-[0.875vw] text-gray-500">${refund.processing_fee || 'N/A'}</td>
                            <td className="px-3 lg:px-[0.75vw] py-4 lg:py-[1vw] text-sm lg:text-[0.875vw] text-gray-500">${refund.refund_amount || 'N/A'}</td>
                            <td className="px-3 lg:px-[0.75vw] py-4 lg:py-[1vw] text-sm lg:text-[0.875vw] text-gray-500">
                              <span className={`inline-flex rounded-full lg:rounded-[0.5vw] px-2 lg:px-[0.5vw] text-xs lg:text-[0.75vw] font-semibold leading-5 lg:leading-[1.25vw] ${getRefundStatusBadgeClass(refund.status)}`}>
                                {refund.status || 'N/A'}
                              </span>
                            </td>
                            <td className="px-3 lg:px-[0.75vw] py-4 lg:py-[1vw] text-sm lg:text-[0.875vw] text-gray-500">{moment(refund.createdAt).format('DD-MM-YYYY HH:mm') || 'N/A'}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={9} className="px-3 lg:px-[0.75vw] py-6 text-center text-sm lg:text-[0.875vw] text-gray-500">
                            No refund records found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Car Payment Records Table */}
        <div className="mt-10 lg:mt-[2.5vw] flex flex-col">
          <h1 className="text-[24px] md:text-[36px] lg:text-[2.3vw] text-left font-semibold text-gray-900 mb-4">Car Payment Record</h1>
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="shadow-[1vw] ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <div className="max-h-[520px] overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 text-nowrap lg:py-[0.875vw] pl-2 lg:pl-[1vw] pr-3 lg:pr-[0.75vw] text-left text-sm lg:text-[0.875vw] leading-[1.25vw] font-medium text-gray-500 sm:pl-6">
                          TRANSACTION ID
                        </th>
                        <th scope="col" className="py-3.5 text-nowrap lg:py-[0.875vw] pl-2 lg:pl-[1vw] pr-3 lg:pr-[0.75vw] text-left text-sm lg:text-[0.875vw] leading-[1.25vw] font-medium text-gray-500 sm:pl-6">
                          CAR TYPE
                        </th>
                        <th scope="col" className="py-3.5 text-nowrap lg:py-[0.875vw] pl-2 lg:pl-[1vw] pr-3 lg:pr-[0.75vw] text-left text-sm lg:text-[0.875vw] leading-[1.25vw] font-medium text-gray-500 sm:pl-6">
                          CAR DETAILS
                        </th>
                        <th scope="col" className="py-3.5 text-nowrap lg:py-[0.875vw] pl-1 lg:pl-[0.5vw] pr-2 lg:pr-[0.5vw] text-left text-sm lg:text-[0.875vw] leading-[1.25vw] font-medium text-gray-500 sm:pl-6">
                          WINNING BID
                        </th>
                        <th scope="col" className="py-3.5 text-nowrap lg:py-[0.875vw] pl-2 lg:pl-[1vw] pr-3 lg:pr-[0.75vw] text-left text-sm lg:text-[0.875vw] leading-[1.25vw] font-medium text-gray-500 sm:pl-6">
                          STATUS
                        </th>
                        <th scope="col" className="py-3.5 text-nowrap lg:py-[0.875vw] pl-2 lg:pl-[1vw] pr-3 lg:pr-[0.75vw] text-left text-sm lg:text-[0.875vw] leading-[1.25vw] font-medium text-gray-500 sm:pl-6">
                          CREATED AT
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {carPayments?.data && carPayments.data.length > 0 ? (
                        [...carPayments.data]
                          .sort((a, b) => {
                            const dateA = new Date(a.created_at);
                            const dateB = new Date(b.created_at);
                            return sortOrder === "latest" ? dateB - dateA : dateA - dateB;
                          })
                          .map((transaction, index) => {
                            // Parse details if it's a JSON string
                            let carDetails = null;
                            if (typeof transaction.details === 'string') {
                              try {
                                carDetails = JSON.parse(transaction.details);
                              } catch (e) {
                                carDetails = null;
                              }
                            } else {
                              carDetails = transaction.details;
                            }

                            return (
                              <tr key={index}>
                                <td className="px-3 lg:px-[0.75vw] py-4 lg:py-[1vw] text-sm lg:text-[0.875vw] text-left font-medium text-gray-900 sm:pl-6">
                                  {transaction?.transaction_id || "N/A"}
                                  <svg
                                    className="inline-block ml-1 h-4 w-4 text-gray-400"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path d="M8 2a1 1 0 000 2h2a1 1 0 100-2H8z" />
                                    <path d="M3 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v6h-4.586l1.293-1.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L10.414 13H15v3a2 2 0 01-2 2H5a2 2 0 01-2-2V5zM15 11h2a1 1 0 110 2h-2v-2z" />
                                  </svg>
                                </td>
                                <td className="whitespace-nowrap text-left px-3 lg:px-[0.75vw] py-4 lg:py-[1vw] text-sm lg:text-[0.875vw] text-gray-500">
                                  <span className={`inline-flex rounded-full lg:rounded-[0.5vw] px-2 lg:px-[0.5vw] text-xs lg:text-[0.75vw] font-semibold leading-5 lg:leading-[1.25vw] ${
                                    transaction?.type === 'Import-car' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                                  }`}>
                                    {transaction?.type || "N/A"}
                                  </span>
                                </td>
                                <td 
                                  className="text-left px-3 lg:px-[0.75vw] py-4 lg:py-[1vw] text-sm lg:text-[0.875vw] text-gray-500 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                                  onClick={() => handleCarDetailsClick(transaction)}
                                >
                                  {carDetails ? (
                                    <div>
                                      <div className="font-medium">
                                        {carDetails.make} {carDetails.model}
                                      </div>
                                      <div className="text-xs text-gray-400">
                                        Year: {carDetails.year}
                                      </div>
                                      <div className="text-xs text-blue-500 mt-1">
                                        Click to view details →
                                      </div>
                                    </div>
                                  ) : (
                                    "N/A"
                                  )}
                                </td>
                                <td className="whitespace-nowrap text-left px-3 lg:px-[0.75vw] py-4 lg:py-[1vw] text-sm lg:text-[0.875vw] text-gray-500">
                                  ${parseFloat(transaction?.amount || 0).toLocaleString()}
                                </td>
                                <td className="whitespace-nowrap text-left px-3 lg:px-[0.75vw] py-4 lg:py-[1vw] text-sm lg:text-[0.875vw] text-gray-500">
                                  <span className={`inline-flex rounded-full lg:rounded-[0.5vw] px-2 lg:px-[0.5vw] text-xs lg:text-[0.75vw] font-semibold leading-5 lg:leading-[1.25vw] ${getStatusBadgeClass(transaction?.status)}`}>
                                    {transaction?.status || "N/A"}
                                  </span>
                                </td>
                                <td className="whitespace-nowrap text-left px-3 lg:px-[0.75vw] py-4 lg:py-[1vw] text-sm lg:text-[0.875vw] text-gray-500">
                                  {moment(transaction?.created_at).format("DD-MM-YYYY HH:mm") || "N/A"}
                                </td>
                              </tr>
                            );
                          })
                      ) : (
                        <tr>
                          <td colSpan={6} className="px-3 lg:px-[0.75vw] py-6 text-center text-sm lg:text-[0.875vw] text-gray-500">
                            No car payment records found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TransactionDetailTable;